import { useQuery, useMutation, useQueryClient, QueryKey } from '@tanstack/react-query';
import apiClient from '../apiClient';

// Public query keys (exported for test assertions & cache inspection)
export const budgetItemsKey: QueryKey = ['budget','items'];
export const budgetSummaryKey: QueryKey = ['budget','summary'];

// Internal fetchers
async function fetchItems(params?: BudgetFilters) {
  const qs = buildQuery(params);
  const res = await apiClient.get<any>('finance', `/items${qs}`, {});
  // Backward compatibility: older backend returned raw array; new returns envelope { items:[] }
  if (Array.isArray(res)) return res;
  return res?.items || [];
}
async function fetchSummary(params?: BudgetFilters) {
  const qs = buildQuery(params);
  const res = await apiClient.get<any>('budget', `/summary${qs}`, {});
  return res; // consumer can branch on version
}

// Filter shape
export interface BudgetFilters {
  types?: string[];
  subtypes?: string[];
  frequencies?: string[]; // W,F,M,Q,Y
}

function buildQuery(f?: BudgetFilters){
  if(!f) return '';
  const params: Record<string,string> = {};
  if (f.types?.length) params.types = f.types.join(',');
  if (f.subtypes?.length) params.subtypes = f.subtypes.join(',');
  if (f.frequencies?.length) params.frequencies = f.frequencies.join(',');
  const search = new URLSearchParams(params).toString();
  return search ? `?${search}` : '';
}

// Option builders (allow unit tests to bypass invoking React Query directly)
export const buildBudgetItemsQueryOptions = (enabled: boolean, filters?: BudgetFilters) => ({
  queryKey: [...budgetItemsKey, filters || {}],
  queryFn: ()=> fetchItems(filters),
  enabled,
  staleTime: 60_000, // cache for 60s to avoid refetching on tab switches
});
export const buildBudgetSummaryQueryOptions = (enabled: boolean, filters?: BudgetFilters) => ({
  queryKey: [...budgetSummaryKey, filters || {}],
  queryFn: ()=> fetchSummary(filters),
  enabled,
  staleTime: 60_000,
});

export function useBudgetItems(enabled: boolean, filters?: BudgetFilters){
  return useQuery(buildBudgetItemsQueryOptions(enabled, filters));
}
export function useBudgetSummary(enabled: boolean, filters?: BudgetFilters){
  return useQuery(buildBudgetSummaryQueryOptions(enabled, filters));
}

// Mutation option helpers (kept inline; simple enough)
export function useAddBudgetItem(){
  const qc = useQueryClient();
  return useMutation({
  mutationFn: (body: any)=> apiClient.post('finance','/items',{ body }),
    onSuccess: ()=>{
      qc.invalidateQueries({ queryKey: budgetItemsKey });
      qc.invalidateQueries({ queryKey: budgetSummaryKey });
    }
  });
}
export function useUpdateBudgetItem(id: string){
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: any)=> apiClient.put('finance', `/items/${id}`, { body, headers: body?.version ? { 'If-Match': body.version } : undefined }),
    onSuccess: ()=> {
      qc.invalidateQueries({ queryKey: budgetItemsKey });
      qc.invalidateQueries({ queryKey: budgetSummaryKey });
    }
  });
}
export function useDeleteBudgetItem(id: string){
  const qc = useQueryClient();
  return useMutation({
  mutationFn: (vars: { version?: number } = {})=> apiClient.del('finance', `/items/${id}`, { headers: vars.version ? { 'If-Match': String(vars.version) } : undefined }),
    onSuccess: ()=> {
      qc.invalidateQueries({ queryKey: budgetItemsKey });
      qc.invalidateQueries({ queryKey: budgetSummaryKey });
    }
  });
}
